/* eslint-disable react-refresh/only-export-components */
import { createContext, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import MarkdownText from './MarkdownText';
import { offsetsToRange, rangeRectsRelativeTo, rangeToOffsets } from '../utils/domTextOffsets';

const TASK_KEY = 'task';
const MAX_TASK_LINES = 400;
const MAX_HIGHLIGHTS = 30;

const MARK_COLORS = [
  { id: 'yellow', label: 'Жёлтый', title: 'Закрепить жёлтым маркером' },
  { id: 'green', label: 'Зелёный', title: 'Закрепить зелёным маркером' },
  { id: 'orange', label: 'Оранжевый', title: 'Закрепить оранжевым маркером' },
];

const CollaborativeTaskContext = createContext(null);

function trimTaskText(text) {
  if (!text) return '';
  const lines = text.split('\n');
  if (lines.length <= MAX_TASK_LINES) return text;
  return lines.slice(0, MAX_TASK_LINES).join('\n');
}

function parseHighlights(raw) {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((h) => h && typeof h.start === 'number' && typeof h.end === 'number' && h.end > h.start)
      .slice(0, MAX_HIGHLIGHTS)
      .map((h) => ({
        start: h.start,
        end: h.end,
        color: MARK_COLORS.some((c) => c.id === h.color) ? h.color : 'yellow',
      }));
  } catch {
    return [];
  }
}

function clearMarksOnMap(m) {
  m.set('selectionStart', 0);
  m.set('selectionEnd', 0);
  m.set('highlightsJson', '[]');
}

function rectsForRangeList(root, ranges) {
  if (!root) return [];
  const out = [];
  ranges.forEach((item, index) => {
    const range = offsetsToRange(root, item.start, item.end);
    if (!range) return;
    rangeRectsRelativeTo(root, range).forEach((rect, ri) => {
      out.push({ ...rect, color: item.color || 'live', key: `${item.color || 'live'}-${index}-${ri}` });
    });
  });
  return out;
}

export function CollaborativeTaskProvider({
  ydoc,
  editMode: editModeProp,
  wrapEnabled = true,
  sourceText,
  sourceTaskId,
  copyTablesAsSpaces = false,
  children,
}) {
  const [text, setText] = useState(() => (
    typeof sourceText === 'string' ? trimTaskText(sourceText) : ''
  ));
  const [activeId, setActiveId] = useState(() => (
    typeof sourceTaskId === 'number' ? sourceTaskId : null
  ));
  const [selection, setSelection] = useState({ start: 0, end: 0 });
  const [highlights, setHighlights] = useState([]);
  const [overlayRects, setOverlayRects] = useState([]);
  const highlightRootRef = useRef(null);
  const editMode = !!editModeProp;

  useEffect(() => {
    if (!ydoc) return;
    const m = ydoc.getMap(TASK_KEY);
    const apply = () => {
      const t = m.get('text');
      if (typeof t === 'string') setText(trimTaskText(t));
      const id = m.get('activeTaskId');
      if (typeof id === 'number') setActiveId(id);
      const selStart = m.get('selectionStart');
      const selEnd = m.get('selectionEnd');
      if (typeof selStart === 'number' && typeof selEnd === 'number') {
        setSelection({ start: Math.max(0, selStart), end: Math.max(0, selEnd) });
      }
      setHighlights(parseHighlights(m.get('highlightsJson')));
    };
    apply();
    m.observe(apply);

    const sessionMeta = ydoc.getMap('sessionMeta');
    const handleMeta = (event) => {
      const activeTaskStatement = sessionMeta.get('activeTaskStatement');
      if (typeof activeTaskStatement !== 'string') return;
      const activeTaskId = sessionMeta.get('activeTaskId');
      const existingText = m.get('text');
      const currentTaskId = m.get('activeTaskId');
      const taskChanged = activeTaskId != null && activeTaskId !== currentTaskId;
      const statementOnlyTick =
        !!event?.keysChanged?.has?.('activeTaskStatement') &&
        !event?.keysChanged?.has?.('activeTaskId') &&
        activeTaskId != null &&
        activeTaskId === currentTaskId &&
        existingText !== activeTaskStatement;

      if (!existingText || taskChanged || statementOnlyTick) {
        if (activeTaskId != null) m.set('activeTaskId', activeTaskId);
        m.set('text', activeTaskStatement);
        if (taskChanged) clearMarksOnMap(m);
      }
    };
    sessionMeta.observe(handleMeta);
    handleMeta();

    return () => {
      m.unobserve(apply);
      sessionMeta.unobserve(handleMeta);
    };
  }, [ydoc]);

  // WorkPage: источник текста — API, не sessionMeta. Пока ydoc нет, рисуем локально;
  // после подключения пишем в Y.Map('task'), не затирая чужие маркеры той же задачи.
  useEffect(() => {
    const nextText = typeof sourceText === 'string' ? trimTaskText(sourceText) : '';
    if (!ydoc) {
      if (nextText) setText(nextText);
      if (typeof sourceTaskId === 'number') setActiveId(sourceTaskId);
      return;
    }
    if (!nextText && sourceTaskId == null) return;

    const m = ydoc.getMap(TASK_KEY);
    ydoc.transact(() => {
      const currentId = m.get('activeTaskId');
      const taskChanged = sourceTaskId != null && currentId != null && sourceTaskId !== currentId;
      if (sourceTaskId != null && sourceTaskId !== currentId) {
        m.set('activeTaskId', sourceTaskId);
      }
      if (nextText && m.get('text') !== nextText) {
        m.set('text', nextText);
      }
      if (taskChanged) clearMarksOnMap(m);
    });
  }, [ydoc, sourceText, sourceTaskId]);

  const writeSelection = useCallback((start, end) => {
    setSelection({ start, end });
    if (!ydoc) return;
    const m = ydoc.getMap(TASK_KEY);
    m.set('selectionStart', start);
    m.set('selectionEnd', end);
  }, [ydoc]);

  const writeHighlights = useCallback((next) => {
    const clipped = next.slice(0, MAX_HIGHLIGHTS);
    setHighlights(clipped);
    if (!ydoc) return;
    const m = ydoc.getMap(TASK_KEY);
    m.set('highlightsJson', JSON.stringify(clipped));
  }, [ydoc]);

  const updateSelectionFromDom = useCallback(() => {
    if (editMode) return;
    const root = highlightRootRef.current;
    if (!root) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    if (!root.contains(range.startContainer) || !root.contains(range.endContainer)) return;
    const offsets = rangeToOffsets(root, range);
    if (!offsets) return;
    writeSelection(offsets.start, offsets.end);
  }, [editMode, writeSelection]);

  const pinCurrentSelection = useCallback((color) => {
    const root = highlightRootRef.current;
    let start = selection.start;
    let end = selection.end;
    const sel = window.getSelection();
    if (root && sel && sel.rangeCount > 0) {
      const range = sel.getRangeAt(0);
      if (root.contains(range.startContainer) && root.contains(range.endContainer)) {
        const offsets = rangeToOffsets(root, range);
        if (offsets && offsets.end > offsets.start) {
          start = offsets.start;
          end = offsets.end;
        }
      }
    }
    if (end <= start) return;
    writeHighlights([...highlights, { start, end, color }]);
    writeSelection(0, 0);
    window.getSelection()?.removeAllRanges();
  }, [highlights, selection.start, selection.end, writeHighlights, writeSelection]);

  const clearMarks = useCallback(() => {
    writeHighlights([]);
    writeSelection(0, 0);
    window.getSelection()?.removeAllRanges();
  }, [writeHighlights, writeSelection]);

  const handleTextChange = useCallback((e) => {
    const value = trimTaskText(e.target.value);
    setText(value);
    if (ydoc) {
      const m = ydoc.getMap(TASK_KEY);
      m.set('text', value);
      clearMarksOnMap(m);
    }
  }, [ydoc]);

  const recomputeOverlay = useCallback(() => {
    const root = highlightRootRef.current;
    if (!root) {
      setOverlayRects([]);
      return;
    }
    const items = [
      ...highlights,
      ...(selection.end > selection.start ? [{ ...selection, color: 'live' }] : []),
    ];
    setOverlayRects(rectsForRangeList(root, items));
  }, [highlights, selection]);

  useLayoutEffect(() => {
    recomputeOverlay();
    const root = highlightRootRef.current;
    if (!root || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(() => recomputeOverlay());
    ro.observe(root);
    const imgs = root.querySelectorAll('img');
    imgs.forEach((img) => img.addEventListener('load', recomputeOverlay));
    return () => {
      ro.disconnect();
      imgs.forEach((img) => img.removeEventListener('load', recomputeOverlay));
    };
  }, [recomputeOverlay, text, activeId, wrapEnabled]);

  const hasMarks = highlights.length > 0 || selection.end > selection.start;

  const value = useMemo(() => ({
    editMode,
    wrapEnabled,
    text,
    activeId,
    overlayRects,
    highlightRootRef,
    updateSelectionFromDom,
    handleTextChange,
    pinCurrentSelection,
    clearMarks,
    hasMarks,
    copyTablesAsSpaces: !!copyTablesAsSpaces,
  }), [
    editMode,
    wrapEnabled,
    text,
    activeId,
    overlayRects,
    updateSelectionFromDom,
    handleTextChange,
    pinCurrentSelection,
    clearMarks,
    hasMarks,
    copyTablesAsSpaces,
  ]);

  return (
    <CollaborativeTaskContext.Provider value={value}>
      {children}
    </CollaborativeTaskContext.Provider>
  );
}

function useCollaborativeTask() {
  const ctx = useContext(CollaborativeTaskContext);
  if (!ctx) {
    throw new Error('CollaborativeTaskPanel must be used within CollaborativeTaskProvider');
  }
  return ctx;
}

export function CollaborativeTaskMarkBar() {
  const { editMode, hasMarks, pinCurrentSelection, clearMarks } = useCollaborativeTask();
  if (editMode) return null;

  return (
    <div
      className="collab-task-mark-bar"
      role="toolbar"
      aria-label="Маркеры в тексте задачи"
      title="Маркеры: выделите фрагмент — его увидит партнёр. Цвет закрепляет метку на сессию."
    >
      {MARK_COLORS.map((c) => (
        <button
          key={c.id}
          type="button"
          className={`collab-task-mark-swatch collab-task-mark-swatch--${c.id}`}
          title={c.title}
          aria-label={c.title}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => pinCurrentSelection(c.id)}
        />
      ))}
      <button
        type="button"
        className="btn btn-outline btn-xs btn-icon-only work-block-title-action collab-task-mark-clear"
        disabled={!hasMarks}
        title="Сбросить маркеры"
        aria-label="Сбросить маркеры"
        onMouseDown={(e) => e.preventDefault()}
        onClick={clearMarks}
      >
        <i className="fa-solid fa-eraser ui-icon" aria-hidden />
      </button>
    </div>
  );
}

export default function CollaborativeTaskPanel({ embedded = false }) {
  const {
    editMode,
    wrapEnabled,
    text,
    activeId,
    overlayRects,
    highlightRootRef,
    updateSelectionFromDom,
    handleTextChange,
    copyTablesAsSpaces,
  } = useCollaborativeTask();

  return (
    <div className={`collab-task-panel${embedded ? ' collab-task-panel--embedded' : ''}`}>
      <div className="collab-task-display-wrapper">
        {editMode ? (
          <textarea
            value={text}
            onChange={handleTextChange}
            rows={10}
            className={`output-block collab-task-editor ${wrapEnabled ? 'task-text-wrap' : 'task-text-nowrap'}`}
            placeholder="Вставьте или введите текст условия..."
          />
        ) : (
          <div
            className={`output-block collab-task-display collab-task-markdown-host ${wrapEnabled ? 'task-text-wrap' : 'task-text-nowrap'}`}
            onMouseUp={updateSelectionFromDom}
            onKeyUp={updateSelectionFromDom}
          >
            <div
              ref={highlightRootRef}
              key={String(activeId ?? 'none')}
              className="collab-task-highlight-root collab-task-switch-anim"
            >
              <MarkdownText
                text={text || ''}
                className={`statement-text ${wrapEnabled ? 'task-text-wrap' : 'task-text-nowrap'}`}
                copyTablesAsSpaces={copyTablesAsSpaces}
              />
              <div className="collab-task-highlight-layer" aria-hidden="true">
                {overlayRects.map((r) => (
                  <span
                    key={r.key}
                    className={`collab-task-hl collab-task-hl--${r.color}`}
                    style={{ top: r.top, left: r.left, width: r.width, height: r.height }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
